import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

// extracts the SMTP status code from a transport error message.
// prefers the received code ('got code "550"') over other quoted codes,
// so the expected codes ('Expected response code "250/251/252"') never match
export function extractSmtpErrorCode(errorMessage) {
  if (!errorMessage) return null;

  const gotCode = errorMessage.match(/got code "(\d{3})"/);
  if (gotCode) return gotCode[1];

  const quotedCode = errorMessage.match(/"([45]\d{2})"/);
  if (quotedCode) return quotedCode[1];

  // bare codes only count at the start of an SMTP reply line ('550-...' or
  // '550 ...'), so ports or IDs like 'host "smtp.example.com:465"' never match
  const replyLineCode = errorMessage.match(/(?:^|\n|")([45]\d{2})[ -]/);
  if (replyLineCode) return replyLineCode[1];

  return null;
}

export function getMailErrorExplanation(errorMessage, t) {
  const code = extractSmtpErrorCode(errorMessage);
  if (!code) return null;

  const explanationMap = {
    421: t('SMTP error 421'),
    450: t('SMTP error 450'),
    451: t('SMTP error 451'),
    452: t('SMTP error 452'),
    535: t('SMTP error 535'),
    550: t('SMTP error 550'),
    551: t('SMTP error 551'),
    552: t('SMTP error 552'),
    553: t('SMTP error 553'),
    554: t('SMTP error 554'),
  };

  return (
    explanationMap[code] ||
    (code.startsWith('4')
      ? t('Temporary mail server error', { code })
      : t('Permanent mail server error', { code }))
  );
}

// variant 'tooltip' (default): raw error as text, explanation behind the (i) icon
// variant 'text': explanation as text, raw error behind the (i) icon
const MailErrorInfo = ({ errorMessage, variant = 'tooltip' }) => {
  const { t } = useTranslation();

  const explanation = getMailErrorExplanation(errorMessage, t);

  const text = variant === 'text' && explanation ? explanation : errorMessage;
  const tooltip = variant === 'text' && explanation ? errorMessage : explanation;

  return (
    <span className="inline-flex items-start gap-1">
      <span>{text}</span>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="top">
          <InfoOutlinedIcon
            fontSize="small"
            color="info"
            className="cursor-help shrink-0"
          />
        </Tooltip>
      )}
    </span>
  );
};

export default MailErrorInfo;
